// src/components/ClientFinancialsTab.js
import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions'; // For Firebase Functions
import { doc, onSnapshot, addDoc, setDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, functions } from '../firebase'; // CORRECTED: Import shared functions instance
import { toast } from 'react-toastify';
import ManualFinancialUpload from './ManualFinancialUpload';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
  IconButton
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { Info as InfoIcon } from '@mui/icons-material';

// Financial range options (shared with wizard)
const REVENUE_RANGES = ['<$500K', '$500K–$1M', '$1M–$5M', '$5M–$15M', '$15M–$50M', '$50M–$100M', '$100M–$500M', '$500M–$1B', '$1B+'];
const REVENUE_TRAJECTORIES = ['Growing >20%', 'Growing <20%', 'Flat', 'Declining'];
const PROFITABILITY_STATUSES = ['Profitable', 'Breakeven', 'Burning Cash'];
const CASH_RUNWAYS = ['Comfortable 12+ months', '6–12 months', '<6 months', 'N/A – Profitable'];
const REVENUE_MODELS = ['Recurring/Subscription', 'Project-Based', 'Product Sales', 'Mixed'];
const GROSS_MARGIN_RANGES = ['<30%', '30–50%', '50–70%', '70–85%', '>85%'];
const HEADCOUNT_RANGES = ['1–10', '11–50', '51–200', '201–500', '501–1000', '>1000'];
const FUNDING_STAGES = ['Bootstrapped', 'Pre-seed/Seed', 'Series A', 'Series B+', 'Private Equity owned', 'N/A'];

const ClientFinancialsTab = ({ user, selectedClient, clientId, clientIsActive, onFinancialUploadSuccess }) => {
    const navigate = useNavigate();
    const [isConnectingToQB, setIsConnectingToQB] = useState(false);
    const [isFetchingQBData, setIsFetchingQBData] = useState(false);
    const [isDisconnectingQB, setIsDisconnectingQB] = useState(false);
    const [quickBooksStatus, setQuickBooksStatus] = useState({
        connected: false,
        realmId: null,
        companyName: null,
        lastConnectionStatus: null,
        displayError: '',
        needsReconnection: false,
    });
    const [uiMessage, setUiMessage] = useState('');
    const [fetchDataMessage, setFetchDataMessage] = useState({ type: '', text: '' });

        const [showFinancialsInfo, setShowFinancialsInfo] = useState(false);

    // Financial Ranges modal state
    const [rangesModalOpen, setRangesModalOpen] = useState(false);
    const [revenueRange, setRevenueRange] = useState('');
    const [revenueTrajectory, setRevenueTrajectory] = useState('');
    const [profitabilityStatus, setProfitabilityStatus] = useState('');
    const [cashRunway, setCashRunway] = useState('');
    const [revenueModel, setRevenueModel] = useState('');
    const [grossMarginRange, setGrossMarginRange] = useState('');
    const [headcountRange, setHeadcountRange] = useState('');
    const [fundingStage, setFundingStage] = useState('');
    const [isSubmittingRanges, setIsSubmittingRanges] = useState(false);

    const handleOpenRangesModal = () => {
      // Pre-populate from existing client data
      if (selectedClient) {
        setRevenueRange(selectedClient.estimatedRevenueRange || '');
        setRevenueTrajectory(selectedClient.estimatedRevenueTrajectory || '');
        setProfitabilityStatus(selectedClient.estimatedProfitabilityStatus || '');
        setCashRunway(selectedClient.estimatedCashRunway || '');
        setRevenueModel(selectedClient.estimatedRevenueModel || '');
        setGrossMarginRange(selectedClient.estimatedGrossMarginRange || '');
        setHeadcountRange(selectedClient.estimatedHeadcountRange || '');
        setFundingStage(selectedClient.estimatedFundingStage || '');
      }
      setRangesModalOpen(true);
    };

    const handleSubmitRanges = async () => {
      if (!revenueRange) {
        toast.error('Please select at least an annual revenue range.');
        return;
      }
      setIsSubmittingRanges(true);
      try {
        const clientRef = doc(db, 'clients', clientId);
        await updateDoc(clientRef, {
          estimatedRevenueRange: revenueRange,
          estimatedRevenueTrajectory: revenueTrajectory || null,
          estimatedProfitabilityStatus: profitabilityStatus || null,
          estimatedCashRunway: cashRunway || null,
          estimatedRevenueModel: revenueModel || null,
          estimatedGrossMarginRange: grossMarginRange || null,
          estimatedHeadcountRange: headcountRange || null,
          estimatedFundingStage: fundingStage || null,
          financialRangesProvided: true,
          financialRangesUpdatedAt: serverTimestamp(),
          financialsSkipped: false,
          financialsUploaded: false,
          financialIntelligenceTier: 'ranges_only',
          financialAnalysisSummary: null,
          financialSummaryLastUpdated: null,
          financialSummarySource: null,
          manualProcessedFinancials: null,
          updatedAt: serverTimestamp()
        });
        toast.success('Financial ranges saved! Updating strategic context...');
        setRangesModalOpen(false);

        // Trigger narrative re-synthesis with the updated ranges as a Strategic Pulse update
        try {
          const rangesParts = [`Annual Revenue: ${revenueRange}`];
          if (revenueTrajectory) rangesParts.push(`Revenue Trajectory: ${revenueTrajectory}`);
          if (profitabilityStatus) rangesParts.push(`Profitability: ${profitabilityStatus}`);
          if (cashRunway) rangesParts.push(`Cash Runway: ${cashRunway}`);
          if (revenueModel) rangesParts.push(`Revenue Model: ${revenueModel}`);
          if (grossMarginRange) rangesParts.push(`Gross Margin: ${grossMarginRange}`);
          if (headcountRange) rangesParts.push(`Headcount: ${headcountRange}`);
          if (fundingStage) rangesParts.push(`Funding Stage: ${fundingStage}`);

          const updateText = `FINANCIAL CONTEXT UPDATE: The client's estimated financial ranges have been updated as follows: ${rangesParts.join(', ')}. Please integrate these financial estimates into the strategic narrative, replacing any previous financial range estimates.`;

          const updateNarrative = httpsCallable(functions, 'updateClientStrategicNarrative');
          await updateNarrative({ clientId, updateText });
          toast.success('Strategic context updated with new financial data.');
        } catch (synthErr) {
          console.warn('[ClientFinancialsTab] Narrative re-synthesis after ranges update failed (non-blocking):', synthErr);
          toast.info('Financial ranges saved. Strategic narrative will update on next signal cycle.');
        }
      } catch (err) {
        console.error('[ClientFinancialsTab] Ranges submit error:', err);
        toast.error('Failed to save ranges. Please try again.');
      } finally {
        setIsSubmittingRanges(false);
      }
    };

    // Determine client's financial tier for conditional rendering
    const financialTier = selectedClient?.financialIntelligenceTier || 'none';
    const getPreviousMonthAndYear = () => {
        const now = new Date();
        let year = now.getFullYear();
        let month = now.getMonth();
        if (month === 0) {
            month = 12;
            year -= 1;
        }
        return { defaultYear: year, defaultMonth: month === 0 ? 12 : month };
    };

    const { defaultYear, defaultMonth } = getPreviousMonthAndYear();
    const [selectedYear, setSelectedYear] = useState(defaultYear);
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
    // NEW: Determine if QB operations should be enabled.
    const clientInSetup = selectedClient?.isSetupComplete === false;
    const allowQuickBooksOperations = clientIsActive || clientInSetup;
        useEffect(() => {
        // ... (useEffect for QB status remains the same)
        if (!clientId) {
            setQuickBooksStatus({
                connected: false, realmId: null, companyName: null,
                lastConnectionStatus: null, displayError: '', needsReconnection: false,
            });
            setFetchDataMessage({ type: '', text: '' });
            return;
        }

        const clientRef = doc(db, 'clients', clientId);
        const unsubscribe = onSnapshot(clientRef, (docSnap) => {
            if (docSnap.exists()) {
                const clientData = docSnap.data();
                const qbConnected = clientData.quickbooksConnected || false;
                const qbRealmId = clientData.quickbooksRealmId || null;
                const qbLastStatus = clientData.quickbooksLastConnectionStatus || null;
                let displayErrorMsg = '';
                let needsReconnectFlag = false;

                if (qbLastStatus && qbLastStatus.startsWith('error_')) {
                    displayErrorMsg = qbLastStatus.replace('error_token_exchange:', 'Token exchange failed. ')
                                            .replace('error_unauthorized:', 'Authorization failed. ')
                                            .replace('error_fetching_company_info:', 'Error fetching company info. ')
                                            .replace('error_fetching_reports:', 'Error fetching financial reports. ')
                                            .replace('error_fetching_multi_period_reports:', 'Error fetching multi-period reports. ')
                                            .replace('error_invalid_grant_on_refresh:', 'Connection invalid (refresh failed). ')
                                            .replace('error_invalid_grant_on_inline_refresh:', 'Connection invalid (inline refresh failed). ')
                                            .replace('error_invalid_grant', 'Connection invalid. ')
                                            .replace('error_qb_app_auth_failed_3100:', 'Application authorization failed with QuickBooks. ')
                                            .replace('error_qb_unauthorized_401:', 'QuickBooks reported an unauthorized error (401). ')
                                            .replace('error_qb_forbidden_403:', 'QuickBooks reported a forbidden error (403). ')
                                            .replace(/error_qb_token_invalid_[\w-]+:/,'QuickBooks token invalid/expired. ')
                                            .replace('server_config_error', 'Server configuration error. ')
                                            .replace('state_parsing_error', 'State validation error. ')
                                            .replace('missing_params_from_quickbooks', 'Missing parameters from QuickBooks. ')
                                            .replace('error_not_connected_in_user_record', 'Connection data missing in user profile. ')
                                            .replace('error_missing_access_token', 'Access token missing. ')
                                            .replace('error_missing_refresh_token', 'Refresh token missing. ')
                                            .replace('invalid_state', 'Invalid session state. ');
                    
                    if (qbLastStatus.includes('invalid_grant') || qbLastStatus.includes('unauthorized') ||
                        qbLastStatus.includes('forbidden') || qbLastStatus.includes('token_expired') ||
                        qbLastStatus.includes('token_invalid') || qbLastStatus.includes('app_auth_failed') ||
                        qbLastStatus.includes('state_') || qbLastStatus.includes('missing_access_token') ||
                        qbLastStatus.includes('missing_refresh_token') || qbLastStatus.includes('not_connected_in_user_record') ) { 
                        needsReconnectFlag = true;
                    }
                }
                
                if (qbRealmId && !qbConnected && !needsReconnectFlag && qbLastStatus !== 'disconnected_by_user') {
                    needsReconnectFlag = true;
                    if (!displayErrorMsg) displayErrorMsg = "Connection lost or may have expired.";
                }
                
                setQuickBooksStatus({
                    connected: qbConnected, realmId: qbRealmId,
                    companyName: clientData.quickbooksCompanyName || null,
                    lastConnectionStatus: qbLastStatus, displayError: displayErrorMsg,
                    needsReconnection: needsReconnectFlag,
                });

                const syncStatus = clientData.quickbooksLastSyncStatus;
                const syncTimestamp = clientData.quickbooksLastSyncTimestamp;
                if (syncStatus && qbConnected) {
                    if (syncStatus.startsWith('error_')) {
                        setFetchDataMessage({type: 'error', text: `Last sync: ${syncStatus.replace(/error_[\w_]+:/, 'Error: ').replace('error_', 'Error: ')}`});
                    } else if (syncStatus.includes('success_company_info_fetched_name_missing')) {
                         setFetchDataMessage({type: 'warning', text: `Last sync: CompanyInfo fetched, name missing. ${syncTimestamp ? new Date(syncTimestamp.seconds * 1000).toLocaleString() : ''}`});
                    } else if (syncStatus.startsWith('success_')) {
                         setFetchDataMessage({type: 'success', text: `Last sync: ${syncStatus.replace('success_', '')}. ${syncTimestamp ? new Date(syncTimestamp.seconds * 1000).toLocaleString() : 'N/A'}`});
                    }
                } else if (!qbConnected) {
                    setFetchDataMessage({ type: '', text: '' });
                } else if (syncTimestamp && qbConnected) {
                     setFetchDataMessage({type: 'info', text: `Data last processed on: ${new Date(syncTimestamp.seconds * 1000).toLocaleString()}`});
                }
            } else {
                setQuickBooksStatus({ connected: false, realmId: null, companyName: null, lastConnectionStatus: null, displayError: 'Client data not found.', needsReconnection: false });
                setFetchDataMessage({ type: '', text: '' });
            }
        }, (error) => {
            console.error("Error listening to client document for QB status:", error);
            toast.error("Could not fetch real-time QuickBooks connection status.");
            setQuickBooksStatus(prev => ({ ...prev, displayError: "Failed to listen for QB status updates.", needsReconnection: false }));
        });
        return () => unsubscribe();
    }, [clientId]);

    useEffect(() => {
        // ... (useEffect for redirect params remains the same)
        const params = new URLSearchParams(window.location.search);
        const qbRedirectStatus = params.get('qb_status');
        const qbRedirectMessage = params.get('message');
        const qbRedirectClientId = params.get('clientId');

        if (qbRedirectStatus && qbRedirectClientId === clientId) {
            if (qbRedirectStatus === 'success') {
                toast.success(qbRedirectMessage || 'QuickBooks connected successfully via redirect!');
                setUiMessage(''); 
            } else if (qbRedirectStatus === 'error') {
                const redirectErrorMsg = `QuickBooks connection error: ${qbRedirectMessage || 'Unknown error during redirect.'}`;
                if (!quickBooksStatus.displayError) { 
                    setUiMessage(redirectErrorMsg);
                }
                toast.error(redirectErrorMsg);
            }
            if (window.history.replaceState) {
        const params2 = new URLSearchParams(window.location.search);
        params2.delete('qb_status');
        params2.delete('message');
        const newSearch = params2.toString();
        const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
        }
    }, [clientId, quickBooksStatus.displayError]);

    const handleConnectQuickBooks = async () => {
        // ... (content remains the same, uses local 'functions' instance now)
        console.log('Starting QuickBooks connection (or reconnection) for client:', clientId);
        if (!clientId) {
            toast.error('No client selected. Cannot connect to QuickBooks.');
            return;
        }
        if (!allowQuickBooksOperations) {
        toast.warn('Client must be active or in setup to connect to QuickBooks.');
        return;
        }
        setIsConnectingToQB(true);
        setUiMessage(''); 
        setQuickBooksStatus(prev => ({ ...prev, displayError: '', needsReconnection: false }));
        setFetchDataMessage({ type: '', text: '' });
        try {
            const initiateAuth = httpsCallable(functions, 'initiateQuickBooksAuth');
            const result = await initiateAuth({ forClientId: clientId, inSetupFlow: clientInSetup });
            if (result.data && result.data.success && result.data.authUrl) {
                window.location.href = result.data.authUrl;
            } else {
                const errMsg = result.data?.message || 'Could not retrieve authorization URL.';
                toast.error(`Connection failed: ${errMsg}`);
                setQuickBooksStatus(prev => ({ ...prev, displayError: errMsg, needsReconnection: true }));
            }
        } catch (error) {
            const errMsg = error.message || 'An unknown error occurred.';
            toast.error(`Error initiating QuickBooks connection: ${errMsg}`);
            setQuickBooksStatus(prev => ({ ...prev, displayError: `Error initiating connection: ${errMsg}`, needsReconnection: true }));
        } finally {
            setIsConnectingToQB(false);
        }
    };

    const handleFetchQuickBooksData = async () => {
        // ... (logic for creating documents from QB reports remains the same, uses local 'functions' instance now)
        if (!quickBooksStatus.realmId || !clientId) {
            toast.error("Cannot fetch data: Missing Realm ID or Client ID.");
            return;
        }
        if (!allowQuickBooksOperations) {
        toast.warn("Client must be active or in setup to fetch QuickBooks data.");
        return;
     }
        if (!selectedYear || !selectedMonth) {
            toast.error("Please select a 'Most Recently Closed Month and Year' to fetch data for.");
            return;
        }

        const lastDayOfMonth = new Date(selectedYear, selectedMonth, 0).getDate();
        const userSpecifiedPeriodEndStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

        setIsFetchingQBData(true);
let toastId = null;
toastId = toast.loading(`Fetching QuickBooks data up to ${selectedMonth}/${selectedYear}...`); // Show loading toast
try {
            const fetchQBDataFunction = httpsCallable(functions, 'WorkspaceQuickBooksFinancials');
            const result = await fetchQBDataFunction({
    clientId: clientId,
    userSpecifiedPeriodEnd: userSpecifiedPeriodEndStr
});

            if (result.data && result.data.success) {
                const missingReports = Array.isArray(result.data?.missingReports) ? result.data.missingReports : [];
                const baseMsg = result.data.message || 'QuickBooks data fetched successfully.';
                const suffix = missingReports.length ? ` (partial: missing ${missingReports.join(', ')})` : '';
                const msg = `${baseMsg}${suffix}`;

                setFetchDataMessage({ type: 'success', text: msg });
                toast.update(toastId, { render: msg, type: "success", isLoading: false, autoClose: 5000 });

                if (onFinancialUploadSuccess) {
                    onFinancialUploadSuccess();
                }

                // Only redirect to Account tab if client setup is incomplete
                if (selectedClient && selectedClient.isSetupComplete === false) {
                    setTimeout(() => {
                        navigate('/', { state: { targetTab: 3 } });
                        toast.info('Redirecting to Account page to continue setup...');
                    }, 1500);
                }

            } else {
                const errorMsg = result.data?.message || "Failed to fetch data from QuickBooks (backend indicated non-success or missing data).";
                toast.update(toastId, { render: errorMsg, type: "error", isLoading: false, autoClose: 5000 });
                setFetchDataMessage({ type: 'error', text: errorMsg });
                if (result.data?.needsReAuth === true) { 
                    setQuickBooksStatus(prev => ({...prev, needsReconnection: true, displayError: "Authorization issue indicated by server. Please reconnect."}));
                }
            }
        } catch (error) {
            let errorMsg = error.message || "An unknown error occurred while fetching data.";
            if (error.details && typeof error.details === 'object' && error.details.message) {
                errorMsg = error.details.message;
            } else if (typeof error.details === 'string') {
                errorMsg = error.details;
            }
            toast.update(toastId, { render: errorMsg, type: "error", isLoading: false, autoClose: 5000 });
            setFetchDataMessage({ type: 'error', text: `Error: ${errorMsg}` });
            const needsReAuth = error.code === 'unauthenticated' || 
                                error.code === 'permission-denied' ||
                                (error.details && typeof error.details === 'object' && error.details.needsReAuth === true);
        if (needsReAuth) {
            setQuickBooksStatus(prev => ({ ...prev, needsReconnection: true, displayError: "Authorization failed or expired. Please reconnect." }));
            }
        } finally {
            setIsFetchingQBData(false);
        }
    };

    const handleDisconnectQuickBooks = async () => {
        // ... (uses local 'functions' instance now, and corrected payload)
        if (!clientId) {
            toast.error("Client ID is missing. Cannot disconnect.");
            return;
        }
        if (!quickBooksStatus.realmId) { // Ensure realmId is available for the call
            toast.error("QuickBooks Realm ID is missing. Cannot disconnect properly.");
            return;
        }
        if (!window.confirm("Are you sure you want to disconnect from QuickBooks? This will remove our access to your financial data and delete previously fetched QuickBooks data from our system.")) {
            return;
        }
        setIsDisconnectingQB(true);
        const toastId = toast.loading("Disconnecting from QuickBooks...");
        try {
            const revokeAccess = httpsCallable(functions, 'revokeQuickBooksAccess');
            // MODIFIED: Corrected payload to only send realmId
            const result = await revokeAccess({ clientId: selectedClient.id }); 
            
            if (result.data && result.data.success) {
                toast.update(toastId, { render: "QuickBooks disconnected successfully!", type: "success", isLoading: false, autoClose: 5000 });
                setUiMessage('');
                setFetchDataMessage({ type: '', text: '' });
                // The onSnapshot listener for the client document should update the QB status
            } else {
                const errMsg = result.data?.message || "Could not disconnect QuickBooks. Please try again on the server.";
                toast.update(toastId, { render: errMsg, type: "error", isLoading: false, autoClose: 5000 });
            }
        } catch (error) {
            const errMsg = error.message || "An unexpected error occurred during disconnect.";
            toast.update(toastId, { render: `Disconnect failed: ${errMsg}`, type: "error", isLoading: false, autoClose: 5000 });
        } finally {
            setIsDisconnectingQB(false);
        }
    };

    const handleFinancialUploadComplete = async () => {
    console.log("Manual financial document upload completed in ClientFinancialsTab.");
    
    // Phase 4D: Flag that new financials need admin processing
    // This flips the tier badge to "Pending Reprocessing" (amber)
    const effectiveClientId = clientId || selectedClient?.id;
    if (effectiveClientId) {
      try {
        const clientRef = doc(db, 'clients', effectiveClientId);
        await updateDoc(clientRef, {
          financialsPendingReprocessing: true,
          financialsPendingReprocessingAt: serverTimestamp()
        });
        console.log(`[Phase4D] Set financialsPendingReprocessing for client ${effectiveClientId}`);
        
        // Notify admin if this is a post-setup upload (client already active)
        if (selectedClient?.isActive === true && selectedClient?.isSetupComplete === true) {
          try {
            const notifyAdmin = httpsCallable(functions, 'notifyAdminFinancialsUploaded');
            await notifyAdmin({ clientId: effectiveClientId });
            console.log(`[Phase4D] Admin notification sent for post-setup financial upload`);
          } catch (notifyError) {
            // Non-blocking — flag was set, notification is best-effort
            console.warn('[Phase4D] Admin notification failed (non-blocking):', notifyError.message);
          }
        }
      } catch (error) {
        console.error('[Phase4D] Error setting reprocessing flag:', error);
      }
    }

    // Since ManageClientsPage doesn't pass this callback, this is just defensive
    if (onFinancialUploadSuccess && selectedClient?.isSetupComplete === false) {
        onFinancialUploadSuccess();
    }
};

    const renderStatus = () => {
        // ... (content remains the same)
        let statusBoxClass = "p-3 border rounded-md ";
        let statusMessage = "";

        if (quickBooksStatus.connected && !quickBooksStatus.needsReconnection) {
            statusBoxClass += "bg-green-100 border-green-300 text-green-700";
            statusMessage = `Status: Connected to QuickBooks.`;
            if (quickBooksStatus.companyName) statusMessage += ` Company: ${quickBooksStatus.companyName}`;
            else statusMessage += ` Company Name: (Fetching or not available)`;
            if (quickBooksStatus.realmId) statusMessage += ` (Realm ID: ${quickBooksStatus.realmId})`;
        } else if (quickBooksStatus.displayError && quickBooksStatus.lastConnectionStatus !== 'disconnected_by_user') {
            statusBoxClass += "bg-red-100 border-red-300 text-red-700";
            statusMessage = `Status: Error - ${quickBooksStatus.displayError}`;
            if (quickBooksStatus.needsReconnection) {
                statusMessage += " Please try reconnecting.";
            }
        } else if (quickBooksStatus.needsReconnection && quickBooksStatus.lastConnectionStatus !== 'disconnected_by_user') {
            statusBoxClass += "bg-yellow-100 border-yellow-400 text-yellow-700";
            statusMessage = "Status: QuickBooks connection needs attention. Please reconnect.";
        } else if (uiMessage && quickBooksStatus.lastConnectionStatus !== 'disconnected_by_user') { 
             statusBoxClass += "bg-blue-100 border-blue-300 text-blue-700";
             statusMessage = uiMessage;
        }
        else {
            statusBoxClass += "bg-gray-100 border-gray-300 text-gray-700";
            statusMessage = "Status: Not Connected to QuickBooks.";
            if (quickBooksStatus.lastConnectionStatus === 'disconnected_by_user') {
                statusMessage = "Status: QuickBooks successfully disconnected.";
            }
        }
        return <div className={statusBoxClass}>{statusMessage}</div>;
    };

    const showConnectButton = !quickBooksStatus.connected || quickBooksStatus.needsReconnection;
    const connectButtonText = quickBooksStatus.realmId && showConnectButton ? 'Reconnect to QuickBooks' : 'Connect to QuickBooks';
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i);
    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
        { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
        { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    return (
        <Fragment>
                <div className="p-4">
            <Box sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Financial Data</Typography>
                    <IconButton size="small" onClick={() => setShowFinancialsInfo(prev => !prev)}>
                        <InfoIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Collapse in={showFinancialsInfo}>
                    <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowFinancialsInfo(false)}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Three ways to add financial context:</strong> Connect QuickBooks for the most comprehensive analysis, upload a financial PDF for detailed statement analysis, or provide Financial Ranges as quick estimates when detailed financials are not available.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>QuickBooks</strong> provides Full Financial Intelligence — the highest tier. Select the most recently closed month and fetch data to import financial reports automatically.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>PDF Upload</strong> also provides Full Financial Intelligence. Use an electronically generated PDF for best results. Documents are scanned for PII before AI processing.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Financial Ranges</strong> provide estimates when detailed financials are not ready. Only Annual Revenue is required. You can upgrade to full financials at any time by connecting QuickBooks or uploading a PDF.
                        </Typography>
                    </Alert>
                </Collapse>
            </Box>

            <div className="flex flex-col lg:flex-row lg:space-x-6">
                {/* Column 1: QuickBooks */}
                <div className="lg:w-1/3">
                    <section className="p-4 sm:p-6 bg-white shadow rounded-lg h-full">
                        <h3 className="text-lg sm:text-xl font-semibold mb-4 text-gray-800">QuickBooks Online Integration</h3>
                        <div className="mb-4">
                            {renderStatus()}
                        </div>

                        {showConnectButton && (
                            <button
                                onClick={handleConnectQuickBooks}
                                disabled={isConnectingToQB || !allowQuickBooksOperations || isFetchingQBData || isDisconnectingQB}
                                className="px-4 sm:px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isConnectingToQB ? 'Connecting...' : connectButtonText}
                            </button>
                        )}

                        {quickBooksStatus.connected && !quickBooksStatus.needsReconnection && (
                            <>
                                <div className="my-4 p-3 sm:p-4 border rounded-md bg-gray-50">
                                    <p className="text-xs sm:text-sm text-gray-700 mb-2">
                                        Select the most recently closed month and year for which you want to fetch financial data.
                                        We will fetch data for that month (and its preceding months in the current quarter if applicable), plus 7 prior full quarters.
                                    </p>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                                        <div>
                                            <label htmlFor="closedMonth" className="block text-xs sm:text-sm font-medium text-gray-700">Closed Month:</label>
                                            <select
                                                id="closedMonth"
                                                name="closedMonth"
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                disabled={isFetchingQBData || !allowQuickBooksOperations || isDisconnectingQB}
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-xs sm:text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:opacity-70"
                                            >
                                                {months.map(month => (
                                                    <option key={month.value} value={month.value}>{month.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="closedYear" className="block text-xs sm:text-sm font-medium text-gray-700">Closed Year:</label>
                                            <select
                                                id="closedYear"
                                                name="closedYear"
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                disabled={isFetchingQBData || !allowQuickBooksOperations || isDisconnectingQB}
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-xs sm:text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md disabled:opacity-70"
                                            >
                                                {years.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4">
                                    <button
                                        onClick={handleFetchQuickBooksData}
                                        disabled={!allowQuickBooksOperations || isConnectingToQB || isFetchingQBData || isDisconnectingQB}
                                        className="px-4 sm:px-6 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50"
                                    >
                                        {isFetchingQBData ? 'Fetching & Saving...' : 'Fetch QuickBooks Data'}
                                    </button>
                                    <button 
                                        onClick={handleDisconnectQuickBooks}
                                        disabled={!allowQuickBooksOperations || isConnectingToQB || isFetchingQBData || isDisconnectingQB}
                                        className="px-4 sm:px-6 py-2 bg-red-500 text-white font-semibold rounded-md hover:bg-red-600 disabled:opacity-50"
                                    >
                                        {isDisconnectingQB ? 'Disconnecting...' : 'Disconnect QuickBooks'}
                                    </button>
                                </div>
                            </>
                        )}

                        {!quickBooksStatus.connected && !quickBooksStatus.needsReconnection && (
                            <button
                                disabled={true}
                                className="mt-4 px-4 sm:px-6 py-2 bg-gray-300 text-gray-500 font-semibold rounded-md cursor-not-allowed"
                            >
                                Fetch QuickBooks Data
                            </button>
                        )}

                        {fetchDataMessage.text && (
                            <div className={`mt-4 p-3 border rounded-md text-xs sm:text-sm ${
                                fetchDataMessage.type === 'error' ? 'bg-red-100 border-red-300 text-red-700' : 
                                fetchDataMessage.type === 'success' ? 'bg-green-100 border-green-300 text-green-700' : 
                                fetchDataMessage.type === 'warning' ? 'bg-yellow-100 border-yellow-400 text-yellow-700' :
                                'bg-blue-100 border-blue-300 text-blue-700'
                            }`}>
                                {fetchDataMessage.text}
                            </div>
                        )}

                        {!allowQuickBooksOperations && (
                            <p className="text-xs sm:text-sm text-orange-600 mt-3">
                                Client is inactive. QuickBooks operations are disabled.
                            </p>
                        )}
                    </section>
                </div>

                {/* Column 2: Manual Upload */}
                <div className="lg:w-1/3 mt-6 lg:mt-0">
                    <section className="p-4 sm:p-6 bg-white shadow rounded-lg h-full">
                        <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-800">Manual Financial Document Upload</h3>
                        {!clientId || !user ? (
                            <p className="text-sm text-gray-500">
                                Please select a client to manage financial data.
                            </p>
                        ) : (
                            <ManualFinancialUpload
                                clientId={clientId}
                                user={user}
                                selectedClient={selectedClient}
                                clientIsActive={clientIsActive}
                                onUploadComplete={handleFinancialUploadComplete}
                            />
                        )}
                    </section>
                </div>

                {/* Column 3: Financial Ranges */}
                <div className="lg:w-1/3 mt-6 lg:mt-0">
                    <section className="p-4 sm:p-6 bg-white shadow rounded-lg h-full">
                        <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-800">Financial Ranges</h3>

                        {!clientId || !user ? (
                            <p className="text-sm text-gray-500">
                                Please select a client to manage financial data.
                            </p>
                        ) : (
                            <>
                                <p className="text-sm text-gray-600 mb-4">
                                    Add estimated financial ranges when detailed financials are not yet available. You can still connect QuickBooks or upload a PDF later.
                                </p>

                                {selectedClient?.estimatedRevenueRange ? (
                                    <div className="p-3 bg-gray-50 rounded-md border text-sm mb-4">
                                        <p><span className="font-medium">Revenue:</span> {selectedClient.estimatedRevenueRange}</p>
                                        {selectedClient?.estimatedRevenueTrajectory && <p><span className="font-medium">Trajectory:</span> {selectedClient.estimatedRevenueTrajectory}</p>}
                                        {selectedClient?.estimatedProfitabilityStatus && <p><span className="font-medium">Profitability:</span> {selectedClient.estimatedProfitabilityStatus}</p>}
                                        {selectedClient?.estimatedRevenueModel && <p><span className="font-medium">Revenue Model:</span> {selectedClient.estimatedRevenueModel}</p>}
                                        {selectedClient?.estimatedCashRunway && <p><span className="font-medium">Cash Runway:</span> {selectedClient.estimatedCashRunway}</p>}
                                        {selectedClient?.estimatedGrossMarginRange && <p><span className="font-medium">Gross Margin:</span> {selectedClient.estimatedGrossMarginRange}</p>}
                                        {selectedClient?.estimatedHeadcountRange && <p><span className="font-medium">Headcount:</span> {selectedClient.estimatedHeadcountRange}</p>}
                                        {selectedClient?.estimatedFundingStage && <p><span className="font-medium">Funding Stage:</span> {selectedClient.estimatedFundingStage}</p>}
                                    </div>
                                ) : (
                                    <div className="p-3 bg-gray-50 rounded-md border text-sm mb-4 text-gray-600">
                                        No financial ranges saved yet.
                                    </div>
                                )}

                                <button
                                    onClick={handleOpenRangesModal}
                                    disabled={!clientIsActive}
                                    className="w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {selectedClient?.estimatedRevenueRange ? 'Update Financial Ranges' : 'Add Financial Ranges'}
                                </button>

                                {!clientIsActive && (
                                    <p className="text-xs sm:text-sm text-orange-600 mt-3">
                                        Client is inactive. Financial ranges cannot be updated.
                                    </p>
                                )}
                            </>
                        )}
                    </section>
                </div>
            </div>
        </div>

        {/* Financial Ranges Modal */}
        <Dialog open={rangesModalOpen} onClose={() => setRangesModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Financial Ranges</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
              Financial context helps SavvaiEdge size strategic recommendations to your client's
              actual situation. Select approximate ranges below — only Annual Revenue is required.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Annual Revenue Range *</InputLabel>
                <MuiSelect value={revenueRange} label="Annual Revenue Range *" onChange={(e) => setRevenueRange(e.target.value)} disabled={isSubmittingRanges}>
                  {REVENUE_RANGES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Revenue Trajectory</InputLabel>
                <MuiSelect value={revenueTrajectory} label="Revenue Trajectory" onChange={(e) => setRevenueTrajectory(e.target.value)} disabled={isSubmittingRanges}>
                  {REVENUE_TRAJECTORIES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Profitability Status</InputLabel>
                <MuiSelect value={profitabilityStatus} label="Profitability Status" onChange={(e) => setProfitabilityStatus(e.target.value)} disabled={isSubmittingRanges}>
                  {PROFITABILITY_STATUSES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Cash Runway</InputLabel>
                <MuiSelect value={cashRunway} label="Cash Runway" onChange={(e) => setCashRunway(e.target.value)} disabled={isSubmittingRanges}>
                  {CASH_RUNWAYS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Primary Revenue Model</InputLabel>
                <MuiSelect value={revenueModel} label="Primary Revenue Model" onChange={(e) => setRevenueModel(e.target.value)} disabled={isSubmittingRanges}>
                  {REVENUE_MODELS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Gross Margin Range</InputLabel>
                <MuiSelect value={grossMarginRange} label="Gross Margin Range" onChange={(e) => setGrossMarginRange(e.target.value)} disabled={isSubmittingRanges}>
                  {GROSS_MARGIN_RANGES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Headcount Range</InputLabel>
                <MuiSelect value={headcountRange} label="Headcount Range" onChange={(e) => setHeadcountRange(e.target.value)} disabled={isSubmittingRanges}>
                  {HEADCOUNT_RANGES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Funding Stage</InputLabel>
                <MuiSelect value={fundingStage} label="Funding Stage" onChange={(e) => setFundingStage(e.target.value)} disabled={isSubmittingRanges}>
                  {FUNDING_STAGES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </MuiSelect>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <MuiButton onClick={() => setRangesModalOpen(false)} disabled={isSubmittingRanges}>
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              onClick={handleSubmitRanges}
              disabled={!revenueRange || isSubmittingRanges}
            >
              {isSubmittingRanges ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Saving...</> : 'Save Ranges'}
            </MuiButton>
          </DialogActions>
        </Dialog>
        </Fragment>
    );
};

export default ClientFinancialsTab;